/**
 * THE RE-ENTRANCY GATE, in one place — because until the final review this
 * package had two answers to the same question and never wrote either down.
 *
 * ── WHY REACT STATE CANNOT DO THIS JOB ──────────────────────────────────────
 * `setBusy(true)` SCHEDULES a render. The second click of a double-click is
 * dispatched before that render happens, so `busy` and the `disabled`
 * attribute the render would have produced are BOTH still stale when it
 * arrives. Task 13 proved this empirically rather than by argument: its DOM
 * suite passed with NO gate at all until the test stopped using two
 * `fireEvent.click` calls (which flush React in between, so the second click
 * lands on an already-disabled button and jsdom drops it) and started
 * dispatching two raw MouseEvents inside one `act()`. Any test of a gate that
 * does not do that is testing `fireEvent`, not the gate.
 *
 * A mutable object read AND written SYNCHRONOUSLY, before the first `await`,
 * is the only thing that makes "this fires once" a property rather than a race
 * the tests happen to win.
 *
 * ── THE RULING, AND ITS TWO TIERS (final review I-2) ────────────────────────
 * Before this module, `lib/jobsView.ts` argued the above at length while
 * eighteen sibling call sites in Start, Sheet, Ebay, MasterData and Staff
 * guarded with `if (shell.busy) return;` — the exact mechanism that docblock
 * rejects — and `refresh-cache` was reachable from two views with two
 * different guarantees. The controller's ruling:
 *
 *   TIER 1 — THE REF GATE (this module), for every action that is
 *   DESTRUCTIVE, EXPENSIVE or EXTERNALLY VISIBLE: anything that spends quota,
 *   writes to a provider, mints a credential, or starts a job. In practice
 *   that is every write this SPA makes, `refresh-cache` included.
 *
 *   TIER 2 — NO GATE OF OUR OWN, for cheap idempotent reads. The only ones in
 *   this SPA are the `refetch()` retry buttons on a failed query, and they are
 *   deliberately ungated because react-query already de-duplicates an in-flight
 *   fetch for a key: a second click joins the first request rather than
 *   issuing one. A gate there would be a second, worse copy of that.
 *
 * `disabled={shell.busy}` stays on the buttons everywhere. It is RENDERING —
 * it tells an operator that something is happening — and it is explicitly NOT
 * the gate, the same distinction `lib/ebayView.ts`'s `enableEbayAction`
 * docblock draws about the confirmation checkbox.
 *
 * ── WHY `lib/jobsView.ts` STILL TAKES THE GATE ITSELF ───────────────────────
 * `runAdminAction`/`runDatevExport` acquire and release `ActionGate` inline
 * rather than through `guardedAction`. Same mechanism, same object type
 * (imported from here), different placement: `spec.confirm` is consulted
 * BETWEEN the check and the send, so that the two consequential actions cannot
 * be run past a "Nein" by a JSX refactor that forgets to ask. Those two are
 * the best-covered orchestrations on the branch and their tests pin the exact
 * ordering, so the gate stayed where their tests can see it.
 */

/**
 * The mutable holder. A `useRef<string | null>(null)` at the call site
 * satisfies it structurally, and so does `{ current: null }` in a Node test —
 * which is the point: nothing here needs a renderer.
 *
 * It holds the NAME of what is running rather than a boolean, so a gate that
 * refuses a click can say what it refused for, and so a stuck gate is legible
 * in a debugger.
 */
export interface ActionGate {
  current: string | null;
}

/**
 * Runs `run` if the gate is free, and refuses otherwise. Returns whether the
 * action was actually started.
 *
 * THE ORDER IS THE WHOLE POINT and it is all synchronous: read the gate, take
 * it, THEN call `run()`. `run()` is invoked in this same turn (not deferred to
 * a microtask), so the action still starts as immediately as it did when the
 * call site called it directly — the only thing that changed is that a second
 * click arriving before the first render cannot get past line one.
 *
 * The gate is released when the action's promise SETTLES, success or failure —
 * the same "always, success or failure" contract `runStaffMutation` and its
 * siblings apply to `setBusy`. A gate that stayed taken after a failed write
 * would be a form that never works again until the panel remounts.
 *
 * A REJECTION RELEASES THE GATE AND IS REPORTED, not swallowed and not
 * rethrown. Every `*Action` orchestration in this SPA handles its own failures
 * and RESOLVES -- with `false`, or with an outcome value -- so a rejection
 * reaching here is a programming error rather than a failed write. Rethrowing
 * it would only produce an unhandled rejection: the same information, with a
 * worse stack, that no test can observe and no reader will attribute. The
 * console is the only sink this SPA has (App.tsx's `ShellErrorBoundary` makes
 * the same call, for the same reason).
 */
export function guardedAction(
  gate: ActionGate,
  name: string,
  run: () => Promise<unknown>,
): boolean {
  if (gate.current !== null) return false;
  gate.current = name;
  void run().then(
    () => { gate.current = null; },
    (error: unknown) => {
      gate.current = null;
      console.error('[admin] guarded action rejected', name, error);
    },
  );
  return true;
}
